import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ShieldCheck, Trash2 } from '@/lib/icons'
import type { AdminListItem } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { AddAdminModal } from './AddAdminModal'
import { createAdminRequest, deleteAdminRequest } from './api'
import { SettingsErrorBanner, SettingsRetryState } from './ErrorStates'
import type { CurrentAdminInfo } from './hooks'
import { useAdmins } from './hooks'

export interface AdminsSectionProps {
  currentAdmin: CurrentAdminInfo
}

/**
 * "👤 Adminlar" bo'limi — `docs/03-app-flow.md` "Sahifa 6": ro'yxat + "[+ Admin qo'shish]".
 * O'zining `useAdmins()` chaqiruvi bor (`components/payouts/PayoutReadyView.tsx`dagi
 * `usePayoutReady()` naqshi bilan bir xil — har bo'lim o'z ma'lumotini o'zi olib keladi).
 * `SettingsPage.tsx` HAM `useAdmins()`ni chaqiradi (super admin ekanini aniqlash uchun) —
 * ikkalasi bitta `['admins']` keshini baham ko'radi, TanStack Query bir xil kalitli
 * so'rovlarni avtomatik birlashtiradi, shuning uchun tarmoqqa ikki marta so'rov ketmaydi.
 */
export function AdminsSection({ currentAdmin }: AdminsSectionProps) {
  const queryClient = useQueryClient()
  const admins = useAdmins()

  const [addOpen, setAddOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<AdminListItem | null>(null)

  const createMutation = useMutation<AdminListItem, Error, { telegramId: number; name: string }>({
    mutationFn: ({ telegramId, name }) => createAdminRequest({ telegramId, name }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admins'] })
      setAddOpen(false)
    },
  })

  const deleteMutation = useMutation<{ ok: true }, Error, number>({
    mutationFn: (telegramId) => deleteAdminRequest(telegramId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admins'] })
      setDeleteTarget(null)
    },
  })

  if (admins.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck size={16} aria-hidden="true" /> Adminlar
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </CardContent>
      </Card>
    )
  }

  if (admins.error && admins.items.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck size={16} aria-hidden="true" /> Adminlar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <SettingsRetryState
            error={admins.error}
            onRetry={() => void admins.refetch()}
            className="min-h-0 py-4"
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>👤 Adminlar</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {admins.error && (
          <SettingsErrorBanner
            error={admins.error}
            isFetching={admins.isFetching}
            onRetry={() => void admins.refetch()}
          />
        )}

        {/* `aria-busy` — fonda qayta so'ralayotganda skrinriderga signal beradi. */}
        <ul className="flex flex-col divide-y divide-app-border" aria-busy={admins.isFetching}>
          {admins.items.map((admin) => {
            // Super adminni o'chirib bo'lmaydi va admin o'zini o'chira olmaydi
            // (`services/admins.ts`dagi ikkita qat'iy server qoidasi) — o'chirish tugmasi
            // shu ikki holatda UMUMAN ko'rsatilmaydi, admin uni bosib, keyin 400/403 xato
            // matnini o'qib o'tirmasin.
            const isSelf = currentAdmin.telegramId === admin.telegramId
            const canDelete = currentAdmin.isSuper && !admin.isSuper && !isSelf

            return (
              <li key={admin.telegramId} className="flex items-center justify-between gap-3 py-2">
                <div className="flex flex-col">
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-app-text">
                    {admin.name ?? "Ism ko'rsatilmagan"}
                    {isSelf && ' (siz)'}
                    {admin.isSuper && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-brand">
                        <ShieldCheck size={12} aria-hidden="true" />
                        super admin
                      </span>
                    )}
                  </span>
                  <span className="tabular text-xs text-app-hint">{admin.telegramId}</span>
                </div>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(admin)}
                    aria-label={`${admin.name ?? 'Admin'}ni o'chirish`}
                    title="O'chirish"
                    className="flex size-11 shrink-0 items-center justify-center rounded-button text-app-hint transition-colors duration-150 ease-out hover:bg-stage-rejected/10 hover:text-stage-rejected-text"
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                )}
              </li>
            )
          })}
        </ul>

        {currentAdmin.isSuper ? (
          <Button variant="secondary" className="self-start" onClick={() => setAddOpen(true)}>
            + Admin qo&apos;shish
          </Button>
        ) : (
          <p className="text-xs text-app-hint">
            Admin qo&apos;shish/o&apos;chirish faqat super admin uchun.
          </p>
        )}
      </CardContent>

      <AddAdminModal
        open={addOpen}
        isSubmitting={createMutation.isPending}
        errorMessage={createMutation.error?.message ?? null}
        onCancel={() => setAddOpen(false)}
        onSubmit={(telegramId, name) => createMutation.mutate({ telegramId, name })}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Adminni o'chirasizmi?"
        description={
          deleteTarget && (
            <>
              <span className="font-medium text-app-text">
                {deleteTarget.name ?? "Ism ko'rsatilmagan"}
              </span>{' '}
              ({deleteTarget.telegramId}) adminlar ro&apos;yxatidan o&apos;chiriladi va endi admin
              panelga kira olmaydi.
            </>
          )
        }
        confirmLabel="Ha, o'chirish"
        tone="danger"
        isLoading={deleteMutation.isPending}
        errorMessage={deleteMutation.error?.message}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.telegramId)}
        onCancel={() => setDeleteTarget(null)}
      />
    </Card>
  )
}
